import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { TrackingEvents } from '@estercare/ester-shared'
import { theme } from '../../../theme/theme'
import { ROUTES } from '../../../constants/routes'
import { CertificationStepLayout } from '../../../components/certification/CertificationStepLayout'
import { SuccessModal } from '../../../components/modals/SuccessModal'
import { OnClickButton } from '../../../components/buttons/onclick/OnClickButton'
import { BodyLarge } from '../../../components/styles/Typography'
import { Arrow } from '../../../components/icons'
import { useSubmitCertificationScreeningMutation } from '../../../api/ester-b2b-api/private/certification/certificationQueries'
import {
  useSelectCertificationPolicyAnswer,
  useSelectCertificationPolicyDocuments,
  useSelectCertificationScreeningId,
} from '../../../state/selectors'
import { useCertificationStore, useScreeningQuestionnaireStore } from '../../../state/store'
import { useEmitEvent } from '../../../hooks/analytics/useEmitEvent'
import { useGoToPreviousQuestion } from '../../../components/screening/useGoToPreviousQuestion'
import { queryErrorHandlerWithMessage } from '../../../utils/queryErrorHandler'
import { shouldOfferPolicyDocumentUpload } from '../../../utils/certification'

export const CertificationSubmit = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const emitEvent = useEmitEvent()

  const screeningId = useSelectCertificationScreeningId()
  const policyAnswer = useSelectCertificationPolicyAnswer()
  const storedDocuments = useSelectCertificationPolicyDocuments()
  const resetCertification = useCertificationStore((state) => state.resetState)
  const resetQuestionnaire = useScreeningQuestionnaireStore((state) => state.resetState)
  const { mutate: submitCertificationScreening, isPending } = useSubmitCertificationScreeningMutation()

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const offersUpload = shouldOfferPolicyDocumentUpload(policyAnswer)
  // Going back and answering the policy question differently leaves documents behind in the store.
  // Deriving what to send keeps a skipped upload from posting a document the admin never confirmed.
  const documents = offersUpload ? storedDocuments : []

  const goToPreviousQuestion = useGoToPreviousQuestion({
    screeningId,
    questionsPath: ROUTES.ADMIN_CERTIFICATION_QUESTIONS,
    fallbackMessage: t('admin.certification.previousQuestionError'),
    onError: setErrorMessage,
  })

  const goBack = async () => {
    if (offersUpload) {
      navigate(ROUTES.ADMIN_CERTIFICATION_UPLOAD)
      return
    }
    await goToPreviousQuestion()
  }

  const submit = () => {
    if (!screeningId) return
    setErrorMessage(null)
    submitCertificationScreening(
      { screeningId, documents },
      {
        onSuccess: () => {
          emitEvent({
            eventType: TrackingEvents.SUBMIT,
            action: 'submit_certification_screening',
            options: { metadata: { documentCount: documents.length.toString() } },
          })
          setIsSubmitted(true)
        },
        onError: (error) => setErrorMessage(queryErrorHandlerWithMessage('submit certification screening', t)(error)),
      }
    )
  }

  const leaveCertification = () => {
    resetCertification()
    resetQuestionnaire()
    navigate(ROUTES.ADMIN_SERVICES)
  }

  if (!screeningId) return <Navigate to={ROUTES.ADMIN_CERTIFICATION} replace />

  return (
    <>
      <CertificationStepLayout
        title={t('admin.certification.submitTitle')}
        goBack={goBack}
        errorMessage={errorMessage}
        primaryAction={
          <OnClickButton
            text={t('admin.certification.submitButton')}
            onClick={submit}
            icon={<Arrow color={theme.color.white} size={15} />}
            disabled={isPending || isSubmitted}
            loading={isPending}
          />
        }
      >
        <BodyLarge>{t('admin.certification.submitDescription')}</BodyLarge>
        <NextSteps>{t('admin.certification.submitNextSteps')}</NextSteps>
      </CertificationStepLayout>
      {isSubmitted && (
        <SuccessModal
          title={t('admin.certification.submittedTitle')}
          text={t('admin.certification.submittedDescription')}
          onClose={leaveCertification}
        />
      )}
    </>
  )
}

const NextSteps = styled(BodyLarge)`
  margin-top: ${theme.spacing.medium}px;
`
